'use client';

import { useState, ReactNode, useEffect } from 'react';
import { productreviewRender } from '@/clicks/productinfoClicks';
import Link from 'next/link';
import SvgIcons from './SvgIcons';

// Define the Product interface
interface Product {
  id: string;
  name: string;
  eng_name: string;
  category: string;
  subcategory: string | null;
  imageurl: string | string[];
  selling_price: string;
  mrp: string;
  discount: string;
  star_rating: number;
  description: string;
  color_options: string[];
  size_options: string[];
  seo_url: string;
  meta_tag_title?: string;
  meta_tag_desc?: string;
  sp: string;
  category_name: string;
  images: string[];
  colorcodes_id: string;
  size_id: string;
  weight_id: string;
  quantity: number;
  addtocart?: boolean;
  shrtdesc: string;
  descp: string;
  variation?: string;
  storeid?: string;
  esin?: string;
  products_id?: string;
  category_id?: string;
  sortorder?: number;
  city_shipping?: string;
  state_shipping?: string;
  national_shipping?: string;
  delivery_time?: string;
  status?: string;
  is_featured?: boolean;
  visibility?: string;
  customizable?: boolean;
  weight?: string;
  gst_rate?: string;
}

// Define the RatingData interface
interface RatingData {
  rating: number;
  feedback: string;
  images: File[];
  timestamp: string;
}

// Define the type for product reviews (replace 'any' with a proper type if possible)
interface ProductReview {
  id: string;
  rating: number;
  feedback: string;
  images?: string[];
  timestamp: string;
  // Add other relevant fields
}

// Define the props for CollapsibleSections
interface CollapsibleSectionsProps {
  product: Product;
  productreviewList: ProductReview[]; // Define the type for productreviewList
  setwritereview: (value: boolean) => void; // Assuming setwritereview is a state setter
  assets: any; // Replace 'any' with a specific type if known
  productaveragerating: number; // Assuming this is a number
  productinfo: any; // Replace 'any' with a specific type if known
  isModalOpen: boolean;
  setIsModalOpen: (value: boolean) => void;
  toggleModal: () => void;
}

export default function CollapsibleSections({
  product,
  productreviewList,
  setwritereview,
  assets,
  productaveragerating,
  productinfo,
  isModalOpen,
  setIsModalOpen,
  toggleModal,
}: CollapsibleSectionsProps) {
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [submittedRatings, setSubmittedRatings] = useState<RatingData[]>([]);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [selectedRatingIndex, setSelectedRatingIndex] = useState<number | null>(null);


  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  const closeFullScreen = () => {
    setIsFullScreen(false);
    setSelectedImageIndex(null);
    setSelectedRatingIndex(null);
  };

  const showPreviousImage = () => {
    if (selectedRatingIndex !== null && selectedImageIndex !== null) {
      const images = submittedRatings[selectedRatingIndex].images;
      setSelectedImageIndex((prev) => (prev === 0 ? images.length - 1 : prev! - 1));
    }
  };

  const showNextImage = () => {
    if (selectedRatingIndex !== null && selectedImageIndex !== null) {
      const images = submittedRatings[selectedRatingIndex].images;
      setSelectedImageIndex((prev) => (prev === images.length - 1 ? 0 : prev! + 1));
    }
  };

  function decodeHtmlEntities(encodedString: any) {
    const textArea = document.createElement("textarea");
    textArea.innerHTML = encodedString;
    return textArea.value;
  }

  return (
    <div className="">
      {/* Full Screen Image Viewer */}
      {isFullScreen && selectedRatingIndex !== null && selectedImageIndex !== null && (
        <div className="fixed inset-0 !bg-[#000000a0] backdrop-blur-sm flex items-center justify-center z-50">
          <div className="relative max-w-4xl w-full md:h-[80vh] h-[50vh] px-4">
            <img
              src={URL.createObjectURL(submittedRatings[selectedRatingIndex].images[selectedImageIndex])}
              alt={`full-screen-${selectedRatingIndex}-${selectedImageIndex}`}
              className="w-full h-full object-contain"
            />
            <button
              onClick={closeFullScreen}
              className="absolute top-4 right-4 text-white !bg-[#00000097] rounded-full p-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6L6 18" />
                <path d="M6 6l12 12" />
              </svg>
            </button>
            {submittedRatings[selectedRatingIndex].images.length > 1 && (
              <>
                <button
                  onClick={showPreviousImage}
                  className="absolute left-1 top-1/2 transform -translate-y-1/2 text-white bg-black/50 rounded-full p-1"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <button
                  onClick={showNextImage}
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 text-white bg-black/50 rounded-full p-1"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
      )}


      {/* Product Description Section */}
      <div className="border-b border-solid border-[#e7e7e7]">
        <button
          className="flex  justify-between items-center md:px-[20px] py-[20px] px-[10px] w-full text-left"
          onClick={() => toggleSection('description')}
        >
          <div className="flex items-start gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide mt-1 lucide-clipboard-list-icon lucide-clipboard-list"
            >
              <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
              <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
              <path d="M12 11h4" />
              <path d="M12 16h4" />
              <path d="M8 11h.01" />
              <path d="M8 16h.01" />
            </svg>
            <div>
              <p className="text-[14px]">Product Description</p>
              <p className="text-[10px] text-[#878787]">Manufacture, Care and Fit</p>
            </div>
          </div>
          <div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`lucide lucide-plus-icon lucide-plus transition-transform ${openSection === 'description' ? 'rotate-45' : ''
                }`}
            >
              <path d="M5 12h14" />
              <path d="M12 5v14" />
            </svg>
          </div>
        </button>
        {openSection === 'description' && (
          <div className="px-0 md:px-4">
            <div className="px-2 product_des" dangerouslySetInnerHTML={{
              __html: decodeHtmlEntities(productinfo?.descp),
            }} />

          </div>
        )}
      </div>

      {/* Shipping & Returns Section */}
      <div className="border-b border-solid border-[#e7e7e7]">
        <button
          className="flex justify-between items-center md:px-[20px] py-[20px] px-[10px] w-full text-left"
          onClick={() => toggleSection('shipping')}
        >
          <div className="flex items-start gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide mt-1 lucide-package-check-icon lucide-package-check"
            >
              <path d="m16 16 2 2 4-4" />
              <path d="M21 10V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l2-1.14" />
              <path d="m7.5 4.27 9 5.15" />
              <polyline points="3.29 7 12 12 20.71 7" />
              <line x1="12" x2="12" y1="22" y2="12" />
            </svg>
            <div>
              <p className="text-[14px]">Shipping & Returns</p>
              <p className="text-[10px] text-[#878787]">Know about return & exchange policy</p>
            </div>
          </div>
          <div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`lucide lucide-plus-icon lucide-plus transition-transform ${openSection === 'shipping' ? 'rotate-45' : ''
                }`}
            >
              <path d="M5 12h14" />
              <path d="M12 5v14" />
            </svg>
          </div>
        </button>
        {openSection === 'shipping' && (
          <div className="p-[20px] text-gray_text">
            <div className='flex flex-wrap'>
              <Link href='/privacy-policy' className='w-4/12 flex flex-col justify-between items-center p-2'>
                <div className='mx-auto h-[50px]'>
                  <SvgIcons.SRIconone />
                </div>
                <div className='h-[30px]'>
                  <p className='text-[#8f98a9] text-[10px] font-semibold text-center'>100% SECURE PAYMENTS</p>
                </div>
              </Link>
              <Link href='/shipping-and-refund-policy' className='w-4/12 flex flex-col justify-between items-center p-2 '>
                <div className='mx-auto h-[50px]'>
                  <SvgIcons.SRIcontwo />
                </div>
                <div className='h-[30px]'>
                  <p className='text-[#8f98a9] text-[10px] font-semibold text-center'>EASY RETURNS & INSTANT REFUNDS</p>
                </div>
              </Link>
              <Link href='/shipping-and-refund-policy' className='w-4/12 flex flex-col justify-between items-center p-2 space-y-2 '>
                <div className='mx-auto h-[50px]'>
                  <SvgIcons.SRIconthree />
                </div>
                <div className='h-[30px]'>
                  <p className='text-[#8f98a9] text-[10px] font-semibold text-center'>SHIPPING GLOBALLY</p>
                </div>
              </Link>

            </div>
          </div>
        )}
      </div>

      {/* Customer Reviews Section */}
      <div className="">
        <button
          className="flex justify-between items-center md:px-[20px] py-[20px] px-[10px] w-full text-left"
          onClick={() => toggleSection('reviews')}
        >
          <div className="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-star-icon lucide-star"
            >
              <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
            </svg>
            <p className="text-[14px]">Customer Reviews</p>
          </div>
          <div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`lucide lucide-plus-icon lucide-plus transition-transform ${openSection === 'reviews' ? 'rotate-45' : ''
                }`}
            >
              <path d="M5 12h14" />
              <path d="M12 5v14" />
            </svg>
          </div>
        </button>
        {openSection === 'reviews' && (
          <div>
            {productreviewRender(
              productreviewList,
              setwritereview,
              assets,
              productaveragerating,
              productinfo,
              isModalOpen,
              setIsModalOpen,
              toggleModal
            )}
          </div>
        )}
      </div>
    </div>
  );
}